import React, { FC, memo, useCallback, useEffect } from "react";
import { useTheme } from "styled-components";
import { Swiper, SwiperSlide } from "swiper/react";
import { Icon, ITheme } from "@evergis/ui";
import { IconTypesKeys } from "@evergis/ui/dist/atoms/Icon/Icon";

import { SwiperContainer } from "./styled";
import { TabAnchor, TabContainer, TabValue } from "../styled";
import { FeatureCardSvg } from "../components/FeatureCardSvg/FeatureCardSvg";
import { TextTrim } from "../components/TextTrim/TextTrim";
import { useWidgetContext } from "../../Dashboard/hooks/useWidgetContext";
import { ContainerProps } from "../types";

export const TabsContainer: FC<ContainerProps> = memo(({ elementConfig, type }) => {
  const { palette } = useTheme() as ITheme;
  const { selectedTabId, setSelectedTabId } = useWidgetContext(type);

  const { options, style, children: tabs } = elementConfig || {};
  const { radius, column, bgColor, noBg, onlyIcon, shownItems, maxLength = 12 } = options || {};

  const renderIcon = useCallback(
    (icon: string, active: boolean) => {
      if (!icon) return null;

      if (!icon.includes(".")) return <Icon kind={icon as IconTypesKeys} />;

      return icon.endsWith(".svg") ? (
        <FeatureCardSvg url={icon} width={16} fontColor={active ? palette.textContrast : palette.textSecondary} />
      ) : (
        <img src={icon} alt="" />
      );
    },
    [palette.textContrast, palette.textSecondary],
  );

  const onClick = useCallback(
    (id: string) => {
      setSelectedTabId(id);
      window.location.hash = `#${id}`;
    },
    [setSelectedTabId],
  );

  useEffect(() => {
    if (!selectedTabId) {
      setSelectedTabId(tabs[0].id);
    }
  }, []);

  return (
    <SwiperContainer style={style}>
      <Swiper spaceBetween={0} slidesPerView={shownItems || 2}>
        {tabs.map(({ id, value, options: tabOptions }) => (
          <SwiperSlide key={id}>
            <TabContainer
              href={`#${id}`}
              active={selectedTabId === id}
              column={column}
              bgColor={bgColor}
              noBg={noBg}
              radius={radius}
              onlyIcon={onlyIcon}
              hasIcon={!!tabOptions?.icon}
              onClick={() => onClick(id)}
            >
              {renderIcon(tabOptions?.icon, selectedTabId === id)}
              {!onlyIcon && (
                <TabValue>
                  <TextTrim maxLength={maxLength}>{value}</TextTrim>
                </TabValue>
              )}
            </TabContainer>
            <TabAnchor id={id} />
          </SwiperSlide>
        ))}
      </Swiper>
    </SwiperContainer>
  );
});
